import {
  Avatar,
  Box,
  Button,
  Divider,
  Grid2,
  IconButton,
  Snackbar,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material';
import styles from '../../styles/styles';
import { useEffect, useState, useMemo } from 'react';
import { useAccount, useBalance, useChainId, useSwitchChain } from 'wagmi';
import { useLiquidInstanceContext } from '../../context/liquidInstanceContext';
import { FriendlyFormatNumber, getTestnetFakeTVL, floorTo, networkToInstanceName, toTokenAmountBigInt } from '../../Utils';
import WrapDialog from './WrapDialog';
import UnwrapDialog from './UnwrapDialog';
import { useParams } from 'react-router-dom';
import { getL1Conf } from '../../conf/liquidInstancesConf';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import YieldTable from './YieldTable';
import usdcIcon from '../../assets/crypto/usdc.svg';
import { useAppContext } from '../../context/context';
import { Instance } from '../../types/liquidInstance';
import { YieldSource } from '../../types/yieldData';
const infoBoxStyles = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: 4,
  justifyContent: 'space-between'
};

const containerStyles = {
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: { xs: 4, md: 10 },
  alignItems: 'center'
} as const;

const headerStyles = {
  width: '100%',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: 1
} as const;

const columnsContainer = {
  display: 'flex',
  flexDirection: { xs: 'column', md: 'row' },
  gap: { xs: 4, md: 10 },
  width: '100%'
} as const;

const rightColumnStyles = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
  minWidth: { xs: '100%', md: '50%' }
} as const;

export default function WrapUnwrap() {
  const [selectedButton, setSelectedButton] = useState<'wrap' | 'unwrap'>('wrap');
  const { address, isConnected } = useAccount();
  const chainId = useChainId();
  const { switchChain } = useSwitchChain();
  const {
    liquidInstanceConfiguration,
    setNormalizedAmount,
    normalizedAmount,
    setWrap,
    setLiquidInstanceConfiguration
  } = useLiquidInstanceContext();
  const [inputValue, setInputValue] = useState<number>(0);
  const [inputString, setInputString] = useState<string>('0');
  const [validInput, setValidInput] = useState(true);
  const [openWrap, setOpenWrap] = useState(false);
  const [openUnwrap, setOpenUnwrap] = useState(false);
  const { id } = useParams();
  const [copySnackOpen, setCopySnackOpen] = useState(false);
  const [buttonDisabled, setButtonDisabled] = useState(true);
  const [TVL, setTVL] = useState<number>(0);
  const [APR, setAPR] = useState<number>(0);
  const { instancesLiveData } = useAppContext();
  const { instances } = useLiquidInstanceContext();
  const { yieldData } = useAppContext();
  const [yieldDataToDisplay, setYieldDataToDisplay] = useState<YieldSource[]>([]);

  useEffect(() => {
    if (yieldData.networks) {
      const yieldDataToDisplay = yieldData.networks[liquidInstanceConfiguration.L2?.name];
      setYieldDataToDisplay(yieldDataToDisplay);
    }
  }, [yieldData, liquidInstanceConfiguration.L2]);

  useEffect(() => {
    if (instancesLiveData) {
      if (instancesLiveData[liquidInstanceConfiguration.L2?.name.toLowerCase()]) {
        setTVL(Number(instancesLiveData[liquidInstanceConfiguration.L2?.name.toLowerCase()]?.l1Tvl) / 1e6);
        setAPR(instancesLiveData[liquidInstanceConfiguration.L2?.name.toLowerCase()]?.bestYield);
      } else {
        console.log('No matching data found for', liquidInstanceConfiguration.L2?.name);
      }
    }
  }, [liquidInstanceConfiguration.L2, instancesLiveData]);

  useEffect(() => {
    if (id) {
      const l1Conf = getL1Conf(id.toLowerCase());
      setLiquidInstanceConfiguration({
        ...liquidInstanceConfiguration,
        L1: l1Conf,
        L2: instances.find((instance: Instance) => instance.name.toLowerCase() === id.toLowerCase()) as Instance
      });
      switchChain({ chainId: l1Conf.chainId });
    }
  }, [id]);

  useEffect(() => {
    if (liquidInstanceConfiguration.L1.chainId !== chainId) {
      switchChain({ chainId: liquidInstanceConfiguration.L1.chainId });
    }
  }, [chainId, liquidInstanceConfiguration.L1.chainId]);

  useEffect(() => {
    if (!isConnected) {
      setInputValue(0);
      setInputString('0');
      setValidInput(true);
    }
  }, [isConnected]);

  useEffect(() => {
    const truncatedInputValue = Number(floorTo(inputValue, 4));
    setNormalizedAmount(toTokenAmountBigInt(truncatedInputValue, liquidInstanceConfiguration.L1.L1TokenDecimals));
  }, [inputValue, liquidInstanceConfiguration.L1.L1TokenDecimals, setNormalizedAmount]);

  useEffect(() => {
    if (normalizedAmount === BigInt(0)) {
      setInputValue(0);
      setInputString('0');
      setValidInput(true);
    }
  }, [normalizedAmount, setInputValue]);

  const handleChange = (newSelectedButton: 'wrap' | 'unwrap') => {
    setSelectedButton(newSelectedButton);
  };
  useEffect(() => {
    setWrap(selectedButton === 'wrap');
  }, [selectedButton, setWrap]);

  useEffect(() => {
    setInputString('0');
    setInputValue(0);
    setValidInput(true);
  }, [selectedButton]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (!/^[0-9]*\.?[0-9]*$/.test(value)) {
      return;
    }

    const digits = value.replace('.', '');
    if (digits.length > 12) {
      return;
    }

    if (value.includes('.') && value.split('.')[1].length > 4) {
      return;
    }

    setInputString(value);
    if (value === '') {
      setInputValue(0);
      setValidInput(true);
    } else {
      setInputValue(Number(value));
      setValidInput(true);
    }
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopySnackOpen(true);
  };
  const handleCloseCopySnack = () => {
    setCopySnackOpen(false);
  };

  const underlyingTokenBalance = useBalance({
    address: address,
    token: liquidInstanceConfiguration.L1.L1TokenAddress,
    chainId: liquidInstanceConfiguration.L1.chainId,
    query: {
      refetchInterval: 5000,
      refetchIntervalInBackground: true
    }
  });
  const wrappedTokenBalance = useBalance({
    address: address,
    token: liquidInstanceConfiguration.L2?.L1WrappedTokenAddress as `0x${string}`,
    chainId: liquidInstanceConfiguration.L1.chainId,
    query: {
      refetchInterval: 5000,
      refetchIntervalInBackground: true
    }
  });

  const normalizedUnderlyingTokenBalance = useMemo(
    () => Number(underlyingTokenBalance.data?.value) / 10 ** liquidInstanceConfiguration.L1.L1TokenDecimals,
    [underlyingTokenBalance.data?.value, liquidInstanceConfiguration.L1.L1TokenDecimals]
  );

  const normalizedWrappedTokenBalance = useMemo(
    () => Number(wrappedTokenBalance.data?.value) / 10 ** liquidInstanceConfiguration.L1.L1TokenDecimals,
    [wrappedTokenBalance.data?.value, liquidInstanceConfiguration.L1.L1TokenDecimals]
  );

  const handleMax = () => {
    if (selectedButton === 'wrap') {
      setInputValue(Number(floorTo(normalizedUnderlyingTokenBalance, 4)));
      setInputString(floorTo(normalizedUnderlyingTokenBalance, 4).toString());
    } else {
      setInputValue(Number(floorTo(normalizedWrappedTokenBalance, 4)));
      setInputString(floorTo(normalizedWrappedTokenBalance, 4).toString());
    }
  };

  useEffect(() => {
    if (
      inputValue === 0 ||
      inputValue > (selectedButton === 'wrap' ? normalizedUnderlyingTokenBalance : normalizedWrappedTokenBalance) ||
      !isConnected
    ) {
      setButtonDisabled(true);
    } else {
      setButtonDisabled(false);
    }
  }, [
    inputValue,
    selectedButton,
    normalizedUnderlyingTokenBalance,
    normalizedWrappedTokenBalance,
    buttonDisabled,
    isConnected
  ]);

  const addTokenToWallet = async () => {
    // Add L1 token
    const l1Token = {
      address: liquidInstanceConfiguration.L2.L1WrappedTokenAddress,
      chainId: liquidInstanceConfiguration.L1.chainId,
      name: liquidInstanceConfiguration.L2.L1WrappedTokenName,
      image: `https://app.relend.network/rusdc/${liquidInstanceConfiguration.L2.name.toLowerCase()}/${networkToInstanceName(liquidInstanceConfiguration.L2.name)}.svg`
    };
    await window.ethereum.request({
      method: 'wallet_watchAsset',
      params: {
        type: 'ERC20',
        options: l1Token
      }
    });

    // Add L2 token
    const l2Token = {
      address: liquidInstanceConfiguration.L2.L2TokenAddress,
      chainId: liquidInstanceConfiguration.L2.L2ChainID,
      name: liquidInstanceConfiguration.L2.L2TokenName,
      image: `/rusdc/${liquidInstanceConfiguration.L2.name.toLowerCase()}/${networkToInstanceName(liquidInstanceConfiguration.L2.name)}.svg`
    };
    await window.ethereum.request({
      method: 'wallet_watchAsset',
      params: {
        type: 'ERC20',
        options: l2Token
      }
    });
  };

  return (
    <>
      {liquidInstanceConfiguration ? (
        <Grid2 container spacing={2} sx={{ paddingTop: { xs: 4, md: 6 } }}>
          <Grid2 {...styles.sideGrids} />
          <Grid2 {...styles.centralGrid}>
            <Box sx={containerStyles}>
              <Box className="header" sx={headerStyles}>
                <Avatar
                  sx={{ width: { xs: 60, md: 80 }, height: { xs: 60, md: 80 } }}
                  src={`/l2/${liquidInstanceConfiguration.L2?.name.toLowerCase()}.svg`}
                />
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {liquidInstanceConfiguration.L2?.L1WrappedTokenName}
                  </Typography>
                  <Typography sx={styles.greyText}>
                    r{liquidInstanceConfiguration.L1.L1Token} {liquidInstanceConfiguration.L2?.name} instance
                  </Typography>
                </Box>
                <Button sx={styles.blueButton} onClick={addTokenToWallet}>
                  Add to Metamask
                </Button>
              </Box>
              <Box sx={columnsContainer}>
                <Box
                  className="left-column"
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    flexGrow: 1,
                    gap: 2,
                    minWidth: { xs: '100%', md: 'auto' }
                  }}
                >
                  <Box
                    className="balance"
                    sx={{
                      ...styles.boxBG,
                      alignItems: 'flex-start',
                      flexGrow: 1,
                      justifyContent: 'flex-start'
                    }}
                  >
                    <Typography sx={{ ...styles.greyText, paddingBottom: 1 }}>Your balance</Typography>
                    {address ? (
                      <Typography
                        title={
                          selectedButton === 'wrap'
                            ? floorTo(normalizedUnderlyingTokenBalance, 2).toString() +
                              ' ' +
                              liquidInstanceConfiguration.L1.L1Token
                            : floorTo(normalizedWrappedTokenBalance, 2).toString() +
                              ' ' +
                              liquidInstanceConfiguration.L2.L2TokenName
                        }
                      >
                        {selectedButton === 'wrap'
                          ? FriendlyFormatNumber(floorTo(normalizedUnderlyingTokenBalance, 2))
                          : FriendlyFormatNumber(floorTo(normalizedWrappedTokenBalance, 2))}{' '}
                        {selectedButton === 'wrap'
                          ? liquidInstanceConfiguration.L1.L1Token
                          : liquidInstanceConfiguration.L2.L2TokenName}
                      </Typography>
                    ) : (
                      <Typography>Please connect your wallet</Typography>
                    )}
                  </Box>
                  <Box
                    className="info"
                    sx={{
                      ...styles.boxBG,
                      width: '100%',
                      height: '100%',
                      flexGrow: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      alignContent: 'space-between',
                      justifyContent: 'space-between'
                    }}
                  >
                    <Box sx={{ ...infoBoxStyles, height: '100%' }}>
                      <Typography sx={styles.greyText}>TVL</Typography>
                      <Typography>
                        {liquidInstanceConfiguration.L2?.live
                          ? '$' + FriendlyFormatNumber(TVL)
                          : '$' + FriendlyFormatNumber(getTestnetFakeTVL(liquidInstanceConfiguration.L2?.L2TokenName))}
                      </Typography>
                    </Box>
                    <Divider sx={styles.divider} />
                    <Box sx={{ ...infoBoxStyles, height: '100%' }}>
                      <Typography title="Ecosystem APY" sx={styles.greyText}>
                        APY
                      </Typography>
                      <Typography title="Ecosystem APY">
                        {APR !== 0
                          ? (APR * 10000).toFixed(2) + '%'
                          : liquidInstanceConfiguration.L2?.live
                            ? 'coming soon'
                            : 'coming soon'}
                      </Typography>
                    </Box>
                    <Divider sx={styles.divider} />
                    <Box sx={{ ...infoBoxStyles, height: '100%' }}>
                      <Typography sx={styles.greyText}>Contract</Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                        <Typography title={liquidInstanceConfiguration.L2?.L1WrappedTokenAddress}>
                          {liquidInstanceConfiguration.L2?.L1WrappedTokenAddress.slice(0, 6)}...
                          {liquidInstanceConfiguration.L2?.L1WrappedTokenAddress.slice(-4)}
                        </Typography>
                        <IconButton
                          size="small"
                          title="Copy contract address"
                          onClick={() => handleCopy(liquidInstanceConfiguration.L2?.L1WrappedTokenAddress)}
                        >
                          <ContentCopyIcon sx={{ color: 'white', fontSize: 12 }} />
                        </IconButton>
                        <IconButton
                          size="small"
                          title="Open in explorer"
                          onClick={() =>
                            window.open(
                              `https://${liquidInstanceConfiguration.L1.chain === 'mainnet' ? '' : liquidInstanceConfiguration.L1.chain + '.'}etherscan.io/address/${liquidInstanceConfiguration.L2?.L1WrappedTokenAddress}`,
                              '_blank'
                            )
                          }
                        >
                          <OpenInNewIcon sx={{ color: 'grey', fontSize: 12 }} />
                        </IconButton>
                      </Box>
                    </Box>
                  </Box>
                </Box>
                <Box className="right-column" sx={rightColumnStyles}>
                  <Box className="selector" sx={{ width: '100%' }}>
                    <ToggleButtonGroup
                      sx={{
                        width: '100%',
                        backgroundColor: 'rgba(255, 255, 255, 0.08)',
                        borderRadius: 10,
                        padding: 1,
                        gap: 1
                      }}
                      value={selectedButton}
                      onChange={(_event, newSelectedButton) => handleChange(newSelectedButton)}
                      exclusive
                    >
                      <ToggleButton
                        sx={{
                          width: '50%',
                          color: 'white',
                          borderRadius: 20,
                          MozOutlineRadiusTopright: 20,
                          padding: 1,
                          fontWeight: 700
                        }}
                        value="wrap"
                      >
                        WRAP
                      </ToggleButton>
                      <ToggleButton
                        sx={{ width: '50%', color: 'white', borderRadius: 20, padding: 1, fontWeight: 700 }}
                        value="unwrap"
                      >
                        UNWRAP
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                  <Box className="user-inputs" sx={{ ...styles.boxBG, width: '100%' }}>
                    <Box
                      sx={{
                        backgroundColor: 'rgba(194, 194, 194, 0.12)',
                        padding: 1,
                        borderRadius: 3,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1
                      }}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1 }}>
                        <Typography>{selectedButton === 'wrap' ? `Wrap` : `Unwrap`}</Typography>
                        <img
                          src={
                            selectedButton === 'wrap'
                              ? usdcIcon
                              : `/rusdc/${liquidInstanceConfiguration.L2.name.toLowerCase()}/${networkToInstanceName(liquidInstanceConfiguration.L2.name)}.svg`
                          }
                          alt="token logo"
                          style={{ width: 30, height: 30 }}
                        />
                        <Typography>
                          {selectedButton === 'wrap'
                            ? liquidInstanceConfiguration.L1.L1Token
                            : liquidInstanceConfiguration.L2.L2TokenName}
                        </Typography>
                      </Box>
                      <TextField
                        id="standard-basic"
                        label=""
                        variant="outlined"
                        value={inputString}
                        onChange={handleInputChange}
                        error={
                          !validInput ||
                          inputValue >
                            (selectedButton === 'wrap'
                              ? normalizedUnderlyingTokenBalance
                              : normalizedWrappedTokenBalance)
                        }
                        helperText={
                          !validInput
                            ? 'Invalid input'
                            : inputValue >
                                (selectedButton === 'wrap'
                                  ? normalizedUnderlyingTokenBalance
                                  : normalizedWrappedTokenBalance)
                              ? 'Insufficient balance'
                              : ''
                        }
                      />
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 10
                        }}
                      >
                        <Typography sx={styles.greyText}>${floorTo(inputValue, 2).toString()}</Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1 }}>
                          <Typography
                            title={
                              selectedButton === 'wrap'
                                ? floorTo(normalizedUnderlyingTokenBalance, 2).toString() +
                                  ' ' +
                                  liquidInstanceConfiguration.L1.L1Token
                                : floorTo(normalizedWrappedTokenBalance, 2).toString() +
                                  ' ' +
                                  liquidInstanceConfiguration.L2.L2TokenName
                            }
                          >
                            {selectedButton === 'wrap'
                              ? FriendlyFormatNumber(
                                  normalizedUnderlyingTokenBalance ? floorTo(normalizedUnderlyingTokenBalance, 4) : 0
                                )
                              : FriendlyFormatNumber(
                                  normalizedWrappedTokenBalance ? floorTo(normalizedWrappedTokenBalance, 4) : 0
                                )}{' '}
                            {selectedButton === 'wrap'
                              ? liquidInstanceConfiguration.L1.L1Token
                              : liquidInstanceConfiguration.L2.L2TokenName}
                          </Typography>
                          <Button
                            sx={{ color: 'white', backgroundColor: 'rgba(255, 255, 255, 0.1)', borderRadius: 2 }}
                            onClick={handleMax}
                            disabled={
                              selectedButton === 'wrap'
                                ? !normalizedUnderlyingTokenBalance
                                : !normalizedWrappedTokenBalance
                            }
                          >
                            MAX
                          </Button>
                        </Box>
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginTop: 3
                      }}
                    >
                      <Typography sx={styles.greyText}>
                        You will {selectedButton === 'wrap' ? 'wrap' : 'unwrap'}
                      </Typography>
                      <Typography>
                        {inputValue.toLocaleString('en-us', { minimumFractionDigits: 2 })}{' '}
                        {selectedButton === 'wrap'
                          ? liquidInstanceConfiguration.L1.L1Token
                          : liquidInstanceConfiguration.L2.L2TokenName}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}
                    >
                      <Typography sx={styles.greyText}>You will receive</Typography>
                      <Typography>
                        {inputValue.toLocaleString('en-us', { minimumFractionDigits: 2 })}{' '}
                        {selectedButton === 'wrap'
                          ? liquidInstanceConfiguration.L2?.L2TokenName
                          : liquidInstanceConfiguration.L1.L1Token}
                      </Typography>
                    </Box>
                    <Button
                      disabled={buttonDisabled}
                      sx={{ ...styles.gradientButton, width: '100%', marginTop: 3 }}
                      onClick={() => (selectedButton === 'wrap' ? setOpenWrap(true) : setOpenUnwrap(true))}
                    >
                      {selectedButton === 'wrap' ? 'Wrap' : 'Unwrap'}
                    </Button>
                  </Box>
                </Box>
              </Box>
              <WrapDialog open={openWrap} onClose={() => setOpenWrap(false)} />
              <UnwrapDialog open={openUnwrap} onClose={() => setOpenUnwrap(false)} />
              <Snackbar
                open={copySnackOpen}
                autoHideDuration={2000}
                onClose={handleCloseCopySnack}
                message="Contract address copied."
              />
              {yieldDataToDisplay && yieldDataToDisplay.length > 0 ? (
                <YieldTable yieldDataToDisplay={yieldDataToDisplay} />
              ) : (
                <></>
              )}
            </Box>
          </Grid2>
          <Grid2 {...styles.sideGrids} />
        </Grid2>
      ) : (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            alignItems: 'center'
          }}
        >
          <Typography>Error, please report this issue to the team.</Typography>
        </Box>
      )}
    </>
  );
}
